// The ground of a board (docs/186 EA4, docs/164 ET6 and ET18): every square's own ground, grounds fraying
// into one another by rank along ragged edges filled from the neighbour's own tile, and banks drawn on the
// liquid from the kit's shore colours. Ported from `tools/encounter-art.py preview`, the art's review board.
import { EDGE_DEPTHS } from '../../../ui/screens/map/groundEdges';
import type { Piece } from '../types';
import { Canvas, hex, type RGBA, type Turn } from './canvas';
import { C, type Ctx } from './context';

/** A square's ground: its name (for ranks) and the drawn tile, turned or not. */
export interface Ground { name: string; piece: Piece; turn?: Turn; key: string }

/** ET18 ranks: the higher ground frays over the lower. Water and land never fray: the bank is between them. */
export const RANK: Record<string, number> = { deep: 0, water: 1, floor: 1, under: 1, floor_patch: 2, rough: 3, mud: 2, lava: 1, acid: 1, floor_dirt: 2, floor_shingle: 2, floor_grass: 3 };
const LIQUID_NAMES = ['water', 'deep', 'lava', 'acid'];

/**
 * EA13: a room's floor the forge names (docs/166 E3). Laid floors (flags, planks, setts, tiles…) keep their own
 * edge, a clean threshold where they meet another floor, as `floor_planks` and the dais always have; soft grounds
 * (earth, grass, shingle) fray as two grounds do (ET18), by rank, over laid floors too.
 */
export const SOFT_FLOORS: ReadonlySet<string> = new Set(['floor_dirt', 'floor_grass', 'floor_shingle']);
const laid = (name: string): boolean => name === 'dais' || name === 'floor_planks' || (name.startsWith('floor_') && name !== 'floor_patch' && !SOFT_FLOORS.has(name));
const LIQUID = new Set(LIQUID_NAMES);
export const isLiquid = (name: string): boolean => LIQUID.has(name);

/** ET14: grounds with no light direction may be flipped and turned for variety (`organic` in the tool). */
export function organic(piece: string): boolean {
  return /^(floor|rough|under|water|deep|mud)/.test(piece) && !/(flags|cobble|plank|tile|sett|board)/.test(piece);
}

const TURNS: readonly (Turn | undefined)[] = [undefined, { flipX: true }, { flipY: true }, { rot90: true }];
/** The turns a ground may take: none on a built kit's floors (lit from the upper left), a left-right flip only where the art has an up (docs/166 C2, the highland and jungle graveyards' `floor_grass`). */
export function turnsOf(ctx: Ctx, piece: string): readonly (Turn | undefined)[] {
  if (ctx.built || !organic(piece)) return [undefined];
  const upright = (ctx.set.prompts['_upright'] as string[] | undefined) ?? [];
  if (upright.includes(piece) || (piece === 'floor_grass' && ctx.set.kit === 'graveyard' && (ctx.set.biome === 'highland' || ctx.set.biome === 'jungle'))) return [undefined, { flipX: true }];
  return TURNS;
}

/** One seeded variant of a ground piece, turned as it may be. */
export function groundOf(ctx: Ctx, name: string, piece: string, x: number, y: number): Ground | undefined {
  const vs = ctx.all(piece);
  if (!vs.length) return undefined;
  const turns = turnsOf(ctx, piece);
  const r = ctx.roll(`g:${piece}`, x, y);
  const p = vs[r % vs.length]!, t = Math.floor(r / vs.length) % turns.length;
  return { name, piece: p, turn: turns[t], key: `${p.frame}/${t}` };
}

/** Paint every square's ground, then fray it by rank (ET18). */
export function paintGrounds(ctx: Ctx, out: Canvas, grounds: Array<Ground | undefined>): void {
  const { w, h } = ctx.sq;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const g = grounds[y * w + x];
    if (g) out.draw(g.piece, x * C, y * C, g.turn, true);
  }
  const SIDES: Array<['n' | 's' | 'w' | 'e', number, number]> = [['n', 0, -1], ['s', 0, 1], ['w', -1, 0], ['e', 1, 0]];
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const g = grounds[y * w + x];
    // a raised step and laid floors keep their own edge (docs/166 C2, EA13); only a soft floor frays over a laid one
    if (!g || g.name === 'dais') continue;
    const rank = RANK[g.name] ?? 1, gLaid = laid(g.name);
    for (const [s, dx, dy] of SIDES) {
      if (!ctx.sq.on(x + dx, y + dy)) continue;
      const n = grounds[(y + dy) * w + x + dx];
      if (!n || isLiquid(n.name) !== isLiquid(g.name) || laid(n.name) || (gLaid && (!SOFT_FLOORS.has(n.name) || g.name === 'floor_planks'))) continue;
      // ET14: an organic ground's variants fray into one another too, or a square of another variant shows as a
      // straight-edged block; which side spills is seeded
      const same = n.name === g.name && n.key !== g.key && !ctx.built && organic(n.piece.piece) && ctx.roll('vf', x + dx, y + dy) > ctx.roll('vf', x, y);
      if ((RANK[n.name] ?? 1) <= rank && !same) continue;
      const prof = EDGE_DEPTHS[ctx.roll(`edge:${s}`, x, y) % EDGE_DEPTHS.length]!;
      for (let i = 0; i < C; i++) {
        for (let k = 0; k < prof[i]!; k++) {
          const [ix, iy] = s === 'n' ? [i, k] : s === 's' ? [i, C - 1 - k] : s === 'w' ? [k, i] : [C - 1 - k, i];
          // the neighbour's tile continued: its ground wraps, so its pixel at the same place carries on
          out.copy(x * C + ix, y * C + iy, n.piece, ix, iy, n.turn);
        }
      }
    }
  }
}

/** The kit's bank colours: land lip, wet line, foam (`_shore_seasons` before `_shore`, docs/166 C2). */
export function shoreOf(ctx: Ctx): [RGBA, RGBA, RGBA] {
  // a place's borrowed water keeps its country's banks (EA14)
  const p = ctx.waterPrompts, season = ctx.waterSeason;
  const seasons = p['_shore_seasons'] as Record<string, string[]> | undefined;
  const s = (season && seasons?.[season]) || (p['_shore'] as string[] | undefined) || (ctx.built ? ['#8e8e96', '#2a3a52', '#c8d4e0'] : ['#e2d6b4', '#3a6a9a', '#f6f9fd']);
  return [hex(s[0]!), hex(s[1]!), hex(s[2]!)];
}

/**
 * Banks (ET6): each liquid pixel takes the bank of its nearest land side or corner: the land itself where it
 * reaches in, then the lip, the wet line and a broken line of foam. `wallLand` counts a wall beside the
 * water as land (built kits: water meets the wall's foot).
 */
export function paintBanks(ctx: Ctx, out: Canvas, grounds: Array<Ground | undefined>, wallLand: (x: number, y: number) => boolean): void {
  const { w, h } = ctx.sq;
  const [sand, wet, foam] = shoreOf(ctx);
  const ORTHO: Array<[string, number, number]> = [['n', 0, -1], ['s', 0, 1], ['w', -1, 0], ['e', 1, 0]];
  const CORNERS: Array<[string, number, number]> = [['ne', 1, -1], ['se', 1, 1], ['sw', -1, 1], ['nw', -1, -1]];
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const g = grounds[y * w + x];
    if (!g || !isLiquid(g.name)) continue;
    const land: Array<{ s: string; n?: Ground }> = [];
    for (const [s, dx, dy] of [...ORTHO, ...CORNERS]) {
      if (!ctx.sq.on(x + dx, y + dy)) continue;
      const n = grounds[(y + dy) * w + x + dx];
      if (n && !isLiquid(n.name)) land.push({ s, n });
      else if (s.length === 1 && wallLand(x + dx, y + dy)) land.push({ s });
    }
    if (!land.length) continue;
    const prof: Record<string, readonly number[]> = {};
    for (const l of land) if (l.s.length === 1) prof[l.s] = EDGE_DEPTHS[ctx.roll(`bank:${l.s}`, x, y) % EDGE_DEPTHS.length]!;
    for (let iy = 0; iy < C; iy++) for (let ix = 0; ix < C; ix++) {
      let best = Infinity, from: Ground | undefined;
      for (const l of land) {
        let kk: number, dd: number;
        if (l.s.length === 1) {
          const [i, k] = l.s === 'n' ? [ix, iy] : l.s === 's' ? [ix, C - 1 - iy] : l.s === 'w' ? [iy, ix] : [iy, C - 1 - ix];
          kk = k; dd = prof[l.s]![i]!;
        } else {
          kk = Math.max(l.s.includes('w') ? ix : C - 1 - ix, l.s.includes('n') ? iy : C - 1 - iy); dd = 3;
        }
        if (kk - dd < best) { best = kk - dd; from = l.n; }
      }
      const X = x * C + ix, Y = y * C + iy;
      if (best < -1 && from) out.copy(X, Y, from.piece, ix, iy, from.turn);
      else if (best < 1) out.set(X, Y, sand);
      else if (best === 1) out.set(X, Y, wet);
      else if (best === 2 && (ix + iy) % 3) out.set(X, Y, foam);
    }
  }
}
